import React from 'react';
import OtpInput from 'react-otp-input';

export interface OtpVerificationProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onResend?: () => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  isResending?: boolean;
  error?: string | null;
  phoneNumber?: string;
  containerStyle?: string;
  inputStyle?: string;
  showCancelButton?: boolean;
  submitButtonText?: string;
  resendButtonText?: string;
  cancelButtonText?: string;
  submitButtonClassName?: string;
  disabled?: boolean;
  className?: string;
}

export const OtpVerification: React.FC<OtpVerificationProps> = ({
  value,
  onChange,
  onSubmit,
  onResend,
  onCancel,
  isSubmitting = false,
  isResending = false,
  error = null,
  phoneNumber,
  containerStyle = 'flex justify-center',
  inputStyle = 'w-10 h-10 text-center border border-input-border-gray rounded-[12px] bg-gray-50 focus:outline-none focus:ring-2 focus:ring-button-primary focus:border-transparent font-["Open_Sans",sans-serif] text-base text-black',
  showCancelButton = false,
  submitButtonText = 'Verify',
  resendButtonText = 'Resend Verification Code',
  cancelButtonText = 'Cancel',
  submitButtonClassName,
  disabled = false,
  className = '',
}) => {
  const handleOtpChange = (newValue: string) => {
    onChange(newValue);
  };

  const handleSubmit = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onSubmit();
  };

  const handleResend = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onResend?.();
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onCancel?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && value.length === 6 && !isSubmitting && !disabled) {
      e.preventDefault();
      onSubmit();
    }
  };

  const isSubmitDisabled = disabled || isSubmitting || value.length !== 6;

  return (
    <div className={`space-y-4 ${className}`}>
      {phoneNumber && (
        <p className="font-open-sans leading-base text-body-gray text-sm font-normal">
          We've sent a 6-digit verification code to <strong>{phoneNumber}</strong>. Please enter it below.
        </p>
      )}

      <div>
        <label className="font-albert-sans text-label-gray mb-2 block text-sm font-medium leading-5">
          Verification Code
        </label>
        <OtpInput
          inputType="number"
          renderSeparator={<span className="mx-2" />}
          value={value}
          onChange={handleOtpChange}
          numInputs={6}
          renderInput={(props) => <input {...props} onKeyDown={handleKeyDown} />}
          skipDefaultStyles={true}
          containerStyle={containerStyle}
          inputStyle={inputStyle}
        />
        {error && (
          <p className="font-open-sans leading-base text-error mt-2 text-center text-sm font-normal">{error}</p>
        )}
      </div>

      <div className="mx-auto max-w-sm space-y-3">
        <div className="flex gap-6">
          {showCancelButton && (
            <button
              type="button"
              onClick={handleCancel}
              className="bg-button-secondary flex-1 rounded-full px-7 py-[10px] text-center disabled:cursor-not-allowed disabled:opacity-50"
              disabled={isSubmitting}
            >
              <span className="text-primary font-albert-sans text-base font-medium leading-5">{cancelButtonText}</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            className={
              submitButtonClassName ||
              `bg-button-primary flex-1 rounded-full px-7 py-[10px] disabled:cursor-not-allowed disabled:opacity-50 ${
                isSubmitDisabled ? 'bg-gray-400' : ''
              }`
            }
            disabled={isSubmitDisabled}
          >
            <span className="font-albert-sans text-base font-medium leading-5 text-white">
              {isSubmitting ? 'Verifying...' : submitButtonText}
            </span>
          </button>
        </div>

        <div className="text-center">
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending}
            className="font-albert-sans hover:text-primary text-button-primary text-base font-medium disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isResending ? 'Sending...' : resendButtonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;
